import { useMemo, useState } from "react";
import { Users, Search, Check, Plus, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { initials, assigneeKey } from "../lib/assignees";

const Row = ({ selected, onClick, children, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick}
    className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-left transition-colors ${selected ? "bg-[#007AFF]/10 text-[#0066CC] dark:text-[#5AA9FF]" : "hover:bg-slate-100 dark:hover:bg-white/5 text-slate-700 dark:text-slate-200"}`}>
    {children}
    {selected && <Check size={14} className="ml-auto shrink-0" />}
  </button>
);

export const AssigneePicker = ({ board, value = [], onChange }) => {
  const [q, setQ] = useState("");
  const keys = useMemo(() => new Set(value.map(assigneeKey)), [value]);
  const toggle = (ref) => {
    const k = assigneeKey(ref);
    onChange(keys.has(k) ? value.filter((a) => assigneeKey(a) !== k) : [...value, ref]);
  };
  const ql = q.trim().toLowerCase();
  const members = (board?.members || []).filter((u) => !ql || u.name.toLowerCase().includes(ql) || u.email.toLowerCase().includes(ql));
  const teams = (board?.teams || []).filter((t) => !ql || t.name.toLowerCase().includes(ql));
  const resolved = value.map((a) => {
    if (a.type === "team") { const t = (board?.teams || []).find((x) => x.id === a.id); return t && { ...a, name: t.name, color: t.color }; }
    const u = (board?.members || []).find((x) => x.id === a.id); return u && { ...a, name: u.name, role: u.role };
  }).filter(Boolean);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {resolved.map((a) => (
        <span key={assigneeKey(a)} data-testid="assignee-chip"
          className="h-7 pl-1 pr-2 rounded-full bg-slate-100 dark:bg-white/10 border border-slate-200/70 dark:border-white/10 text-xs font-medium text-slate-700 dark:text-slate-200 flex items-center gap-1.5">
          {a.type === "team" ? (
            <span className="w-5 h-5 rounded-md text-white flex items-center justify-center" style={{ background: a.color || "#AF52DE" }}><Users size={10} /></span>
          ) : (
            <span className={`w-5 h-5 rounded-full text-white text-[8px] font-bold flex items-center justify-center ${a.role === "admin" ? "bg-slate-900 dark:bg-slate-600" : "bg-[#007AFF]"}`}>{initials(a.name)}</span>
          )}
          {a.name}
          <button type="button" data-testid="assignee-chip-remove" onClick={() => toggle({ type: a.type, id: a.id })} className="text-slate-400 hover:text-[#FF2D55]"><X size={11} /></button>
        </span>
      ))}
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" data-testid="assignee-picker-trigger"
            className="h-7 px-2.5 rounded-full border border-dashed border-slate-300 dark:border-white/20 text-xs font-medium text-slate-500 dark:text-slate-400 hover:border-[#007AFF] hover:text-[#007AFF] flex items-center gap-1 transition-colors">
            <Plus size={12} /> {resolved.length ? "Ekle" : "Kişi veya grup ata"}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-2 rounded-2xl">
          <div className="relative mb-2">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input autoFocus data-testid="assignee-picker-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara…"
              className="w-full h-8 pl-8 pr-2 rounded-lg bg-slate-100/80 dark:bg-white/10 text-xs dark:text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF]/40" />
          </div>
          <div className="max-h-64 overflow-y-auto space-y-0.5">
            {teams.length > 0 && <p className="px-2.5 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Gruplar</p>}
            {teams.map((t) => (
              <Row key={t.id} testId="assignee-picker-team-option" selected={keys.has(`team:${t.id}`)} onClick={() => toggle({ type: "team", id: t.id })}>
                <span className="w-6 h-6 rounded-lg text-white flex items-center justify-center shrink-0" style={{ background: t.color || "#AF52DE" }}><Users size={12} /></span>
                <span className="truncate">{t.name}</span>
                <span className="text-[10px] text-slate-400 shrink-0">{(t.member_ids || []).length} kişi</span>
              </Row>
            ))}
            {members.length > 0 && <p className="px-2.5 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Kişiler</p>}
            {members.map((u) => (
              <Row key={u.id} testId="assignee-picker-user-option" selected={keys.has(`user:${u.id}`)} onClick={() => toggle({ type: "user", id: u.id })}>
                <span className={`w-6 h-6 rounded-full text-white text-[9px] font-bold flex items-center justify-center shrink-0 ${u.role === "admin" ? "bg-slate-900 dark:bg-slate-600" : "bg-[#007AFF]"}`}>{initials(u.name)}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{u.name}</span>
                  <span className="block text-[10px] text-slate-400 truncate">{u.role === "admin" ? "Plena" : u.email}</span>
                </span>
              </Row>
            ))}
            {teams.length === 0 && members.length === 0 && <p className="px-2.5 py-4 text-xs text-slate-400 text-center">Sonuç yok</p>}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default AssigneePicker;
